import React from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const PrivacySection = ({ formData, onChange, validationErrors = {}, isLoading = false }) => {
  const renderFieldError = (fieldName) => {
    const error = validationErrors[fieldName];
    if (error) {
      return (
        <p className="text-sm text-error-600 mt-1">
          {Array.isArray(error) ? error[0] : error}
        </p>
      );
    }
    return null;
  };

  const handleNotificationPreferenceChange = (category, type, value) => {
    const currentPrefs = formData?.notificationPreferences || {};
    const updatedPrefs = {
      ...currentPrefs,
      [category]: {
        ...currentPrefs[category],
        [type]: value
      }
    };
    onChange('notificationPreferences', updatedPrefs);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-accent-800 mb-2">
          Privacy Controls
        </h2>
        <p className="text-accent-600">
          Manage your privacy settings and control how your data is used
        </p>
      </div>

      <div className="space-y-6">
        {/* Push Notification Preferences */}
        <div>
          <h3 className="font-heading font-semibold text-accent-800 mb-4">
            Push Notification Settings
          </h3>

          <div className="space-y-4">
            {/* Push Giveaway Notifications */}
            <div className="flex items-center justify-between p-4 bg-accent-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="Gift" size={20} className="text-accent-600" />
                <div>
                  <h4 className="font-medium text-accent-800">Giveaway Push Notifications</h4>
                  <p className="text-sm text-accent-600">
                    Receive push notifications about new giveaways and contests
                  </p>
                </div>
              </div>
              <Input
                type="checkbox"
                checked={formData?.notificationPreferences?.push?.giveaways || false}
                onChange={(e) => handleNotificationPreferenceChange('push', 'giveaways', e.target.checked)}
                disabled={isLoading}
              />
            </div>

            {/* Push Account Notifications */}
            <div className="flex items-center justify-between p-4 bg-accent-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="Shield" size={20} className="text-accent-600" />
                <div>
                  <h4 className="font-medium text-accent-800">Account Push Notifications</h4>
                  <p className="text-sm text-accent-600">
                    Important account updates and security alerts via push notification
                  </p>
                </div>
              </div>
              <Input
                type="checkbox"
                checked={formData?.notificationPreferences?.push?.account || false}
                onChange={(e) => handleNotificationPreferenceChange('push', 'account', e.target.checked)}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        {/* Privacy Settings */}
        <div>
          <h3 className="font-heading font-semibold text-accent-800 mb-4">
            Privacy & Data Settings
          </h3>

          <div className="space-y-4">
            {/* Public Profile */}
            <div className="flex items-center justify-between p-4 bg-accent-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="Eye" size={20} className="text-accent-600" />
                <div>
                  <h4 className="font-medium text-accent-800">Public Profile</h4>
                  <p className="text-sm text-accent-600">
                    Make your profile visible to other users
                  </p>
                </div>
              </div>
              <Input
                type="checkbox"
                checked={formData?.notificationPreferences?.privacy?.publicProfile || false}
                onChange={(e) => handleNotificationPreferenceChange('privacy', 'publicProfile', e.target.checked)}
                disabled={isLoading}
              />
            </div>

            {/* Analytics Consent */}
            <div className="flex items-center justify-between p-4 bg-accent-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="BarChart3" size={20} className="text-accent-600" />
                <div>
                  <h4 className="font-medium text-accent-800">Analytics Consent</h4>
                  <p className="text-sm text-accent-600">
                    Allow us to collect anonymized usage data to improve our services
                  </p>
                </div>
              </div>
              <Input
                type="checkbox"
                checked={formData?.notificationPreferences?.privacy?.analyticsConsent || false}
                onChange={(e) => handleNotificationPreferenceChange('privacy', 'analyticsConsent', e.target.checked)}
                disabled={isLoading}
              />
            </div>
          </div>
          {renderFieldError('notificationPreferences')}
        </div>

        {/* Account Security */}
        <div>
          <h3 className="font-heading font-semibold text-accent-800 mb-4">
            Account Security
          </h3>

          <div className="space-y-4">
            <div className="p-4 bg-primary-50 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <Icon name="Shield" size={20} className="text-primary-600" />
                <h4 className="font-medium text-primary-800">Account Verification</h4>
              </div>
              <p className="text-sm text-primary-700 mb-3">
                Account Status: {formData?.isVerified ? 'Verified ✓' : 'Pending Verification'}
              </p>
              {!formData?.isVerified && (
                <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                  Resend Verification Email →
                </button>
              )}
            </div>

            <div className="p-4 bg-warning-50 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <Icon name="Key" size={20} className="text-warning-600" />
                <h4 className="font-medium text-warning-800">Password Security</h4>
              </div>
              <p className="text-sm text-warning-700 mb-3">
                Keep your account secure by using a strong password and changing it regularly.
              </p>
              <button className="text-sm text-warning-600 hover:text-warning-700 font-medium">
                Change Password →
              </button>
            </div>
          </div>
        </div>

        {/* Current Privacy Summary */}
        {formData?.notificationPreferences && (
          <div className="bg-primary-50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-primary-800 mb-1">
                  Privacy Summary
                </h4>
                <div className="text-sm text-primary-700 space-y-1">
                  <p>• Profile: {formData.notificationPreferences?.privacy?.publicProfile ? 'Public' : 'Private'}</p>
                  <p>• Analytics: {formData.notificationPreferences?.privacy?.analyticsConsent ? 'Enabled' : 'Disabled'}</p>
                  <p>• Push Notifications: {
                    (formData.notificationPreferences?.push?.giveaways || formData.notificationPreferences?.push?.account)
                      ? 'Enabled' : 'Disabled'
                  }</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Privacy Notice */}
        <div className="bg-accent-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-accent-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-accent-800 mb-1">
                Privacy Notice
              </h4>
              <p className="text-sm text-accent-600 mb-3">
                We are committed to protecting your privacy and only use your data to provide
                and improve our services. You can review our full privacy policy to understand
                how we handle your information.
              </p>
              <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                Read Privacy Policy →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySection;